import React, { useEffect, useState } from "react";
import { useBreadcrumbs } from "@/components/provider/BreadcrumbsProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import {
  useGetIntegrationsQuery,
  usePostAuthZSalesforceMutation,
  usePostAuthZHubSpotMutation,
  usePostAuthZPodioMutation,
  usePostAuthZGhlMutation,
  useSyncIntegrationMutation,
  useDeleteIntegrationMutation,
} from "@/store/rest_api";
import Integration from "@/model/interface/Integration";
import { INTEGRATION_PROVIDERS, providerInitials } from "@/util/integrationProviders";
import toast from "react-hot-toast";

const Integrations = () => {
  const { data: integrations } = useGetIntegrationsQuery();
  const [authZSalesforce, authZSalesforceResponse] = usePostAuthZSalesforceMutation();
  const [authZHubSpot, authZHubSpotResponse] = usePostAuthZHubSpotMutation();
  const [authZPodio, authZPodioResponse] = usePostAuthZPodioMutation();
  const [authZGhl, authZGhlResponse] = usePostAuthZGhlMutation();
  const [syncIntegration, syncIntegrationResponse] = useSyncIntegrationMutation();
  const [deleteIntegration, { isLoading: isDeleting }] = useDeleteIntegrationMutation();
  const [pendingDelete, setPendingDelete] = useState<Integration | null>(null);
  const navigate = useNavigate();
  const { setSection } = useBreadcrumbs();

  const handleDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    try {
      await deleteIntegration(pendingDelete.id).unwrap();
      toast.success("Integration removed");
      setPendingDelete(null);
    } catch {
      toast.error("Failed to remove integration");
    }
  };

  useEffect(() => {
    setSection("Integrations");
  }, []);

  useEffect(() => {
    if (authZSalesforceResponse.isSuccess) {
      window.location.href = authZSalesforceResponse.data.url;
    }
  }, [authZSalesforceResponse.isSuccess]);

  useEffect(() => {
    if (authZHubSpotResponse.isSuccess) {
      window.location.href = authZHubSpotResponse.data.url;
    }
  }, [authZHubSpotResponse.isSuccess]);

  useEffect(() => {
    if (authZPodioResponse.isSuccess) {
      window.location.href = authZPodioResponse.data.url;
    }
  }, [authZPodioResponse.isSuccess]);

  useEffect(() => {
    if (authZGhlResponse.isSuccess) {
      window.location.href = authZGhlResponse.data.url;
    }
  }, [authZGhlResponse.isSuccess]);

  useEffect(() => {
    if (syncIntegrationResponse.isSuccess) {
      toast.success("Successfully synced leads.");
    }
  }, [syncIntegrationResponse.isSuccess]);

  function connectService(integration: Integration) {
    if (integration.service === "salesforce") {
      authZSalesforce(integration);
    } else if (integration.service === "hubspot") {
      authZHubSpot(integration);
    } else if (integration.service === "podio") {
      authZPodio(integration);
    } else if (integration.service === "ghl") {
      authZGhl(integration);
    } else if (
      integration.service === "investorfuse" ||
      integration.service === "carrot" ||
      integration.service === "reisift"
    ) {
      navigate(`/integrations/${integration.id}`);
    }
  }

  const setupRank = (provider: (typeof INTEGRATION_PROVIDERS)[number]) => {
    const integration = integrations?.find((item) => item.service === provider.key);
    const status = integration?.status?.toLowerCase();
    if (status === "active") {
      return 0;
    }
    if (status === "needsreauth") {
      return 1;
    }
    if (integration) {
      return 2;
    }
    return provider.available ? 3 : 4;
  };

  const orderedProviders = [...INTEGRATION_PROVIDERS].sort((a, b) => setupRank(a) - setupRank(b));

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground">Connect your CRM and tools. Activity and contacts sync automatically.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {orderedProviders.map((provider) => {
          const integration = integrations?.find((item) => item.service === provider.key);
          const status = integration?.status?.toLowerCase();
          const isActive = status === "active";
          const needsReauth = status === "needsreauth";
          const syncOff = !!integration && integration.syncEnabled === false;

          return (
            <Card key={provider.key} className={`flex flex-col ${provider.available ? "" : "opacity-60"}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-lg text-sm font-semibold text-white"
                      style={{ backgroundColor: provider.accent }}
                    >
                      {providerInitials(provider.name)}
                    </div>
                    <div>
                      <CardTitle className="text-base">{provider.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{provider.category}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {isActive ? (
                      <Badge className="border-transparent bg-emerald-600 text-white hover:bg-emerald-600">
                        Active
                      </Badge>
                    ) : needsReauth ? (
                      <Badge variant="destructive">Reconnect</Badge>
                    ) : integration ? (
                      <Badge variant="secondary">Setup pending</Badge>
                    ) : provider.available ? (
                      <Badge variant="outline">Available</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Coming soon
                      </Badge>
                    )}
                    {syncOff && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-600 dark:text-amber-400">
                        Sync off
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4">
                <p className="text-sm text-muted-foreground">{provider.description}</p>
                <div className="mt-auto flex flex-wrap gap-2">
                  {!provider.available && (
                    <Button variant="outline" size="sm" disabled>
                      Coming soon
                    </Button>
                  )}
                  {provider.available && isActive && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/integrations/${integration!.id}`)}>
                        Manage
                      </Button>
                      <Button size="sm" onClick={() => syncIntegration(integration!)} disabled={syncOff}>
                        Sync
                      </Button>
                    </>
                  )}
                  {provider.available && !isActive && integration && (
                    <>
                      <Button size="sm" onClick={() => connectService(integration)}>
                        {needsReauth ? "Reconnect" : "Connect"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/integrations/${integration.id}`)}>
                        Manage
                      </Button>
                    </>
                  )}
                  {provider.available && !integration && (
                    <Button size="sm" onClick={() => navigate(`/integrations/new?service=${provider.key}`)}>
                      Add
                    </Button>
                  )}
                  {integration && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setPendingDelete(integration)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this integration?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the <strong>{pendingDelete?.label}</strong> integration and its sync history.
              Contacts already synced are kept, but no further syncing or activity logging will happen. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Integrations;
